import { useCallback } from "react";
import { API_BASE_URL } from "../constants/app.constant";
import { useFetch } from "../hooks/useFetch";
import { APIResponse } from "../interfaces/common.interface";
import useAuthService from "./auth.service";
import { CorpusItem, CorpusStats, PaginationDefaults } from "@/constants/corpus.constants";
import { CorpusQueryParams } from "@/interfaces/corpus.interface";

/**
 * useCorpusApiService hook provides corpus management API service functions.
 * Handles corpus statistics, listing, export, and retry operations.
 * 
 * @hook
 * @returns {Object} Corpus API service methods
 * @returns {Function} returns.getCorpusStats - Get corpus statistics
 * @returns {Function} returns.getCorpuses - Get corpus list with filters and pagination
 * @returns {Function} returns.exportCorpuses - Export corpus data as CSV
 * @returns {Function} returns.triggerCorpusRetry - Trigger retry for failed corpus ingestion
 * 
 * @example
 * ```tsx
 * const { getCorpusStats, getCorpuses, exportCorpuses } = useCorpusApiService();
 * 
 * const stats = await getCorpusStats();
 * const corpuses = await getCorpuses({ page: 1, page_size: 20 });
 * ```
 */
const useCorpusApiService = () => {
  const fetchService = useFetch();
  const { getUserAccessToken } = useAuthService();

  const CORPUS_API_BASE_URL = `${API_BASE_URL}/corpus`;
  const CORPUS_STATS_API = `${API_BASE_URL}/admin/corpus-stats`;
  const CORPUS_EXPORT_API = `${API_BASE_URL}/admin/corpus/export`;
  const CORPUS_TRIGGER_API = `${API_BASE_URL}/admin/corpus/trigger`;

  /**
   * Gets corpus statistics with dynamic keys.
   * Transforms array response to flat object structure.
   * 
   * @param {AbortSignal} [signal] - Optional abort signal for request cancellation
   * @returns {Promise<CorpusStats>} Corpus statistics object
   * @throws {Error} If token is invalid
   */
  const getCorpusStats = useCallback(
    async (signal?: AbortSignal) => {
      const token = await getUserAccessToken();
      if (!token) throw new Error("Invalid token");

      const response = await fetchService.get<APIResponse<CorpusStats[]>>(CORPUS_STATS_API, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        signal,
      });

      if (Array.isArray(response.data)) {
        const mergedStats: CorpusStats = {};
        response.data.forEach((statObj) => {
          Object.assign(mergedStats, statObj);
        });
        return mergedStats;
      }

      return response.data as CorpusStats;
    },
    [fetchService, getUserAccessToken]
  );

  /**
   * Gets corpus list with pagination, search, filters, and sorting.
   * 
   * @param {CorpusQueryParams} [params={}] - Query parameters (page, page_size, sortBy, sortOrder, filters, status, search)
   * @param {AbortSignal} [signal] - Optional abort signal for request cancellation
   * @returns {Promise<APIResponse<CorpusItem[]>>} Array of corpus items
   * @throws {Error} If token is invalid
   */
  const getCorpuses = useCallback(
    async (params: CorpusQueryParams = {}, signal?: AbortSignal) => {
      const token = await getUserAccessToken();
      if (!token) throw new Error("Invalid token");

      const qp = new URLSearchParams({
        page: String(params.page ?? PaginationDefaults.PAGE),
        size: String(params.page_size ?? PaginationDefaults.PAGE_SIZE),
      });

      if (params.sortBy) {
        qp.append("sortBy", params.sortBy);
      }
      if (params.sortOrder) {
        qp.append("sortOrder", params.sortOrder);
      }

      const url = `${API_BASE_URL}/admin/corpuses?${qp.toString()}`;

      const body: any = {};

      if (params.filters) {
        Object.assign(body, params.filters);
      } else {
        if (params.status) {
          body.status = params.status;
        }
        if (params.search) {
          body.name = params.search;
        }
      }

      const resp = await fetchService.post<APIResponse<CorpusItem[]>>(
        url,
        {
          body,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          signal,
        }
      );

      return resp;
    },
    [fetchService, getUserAccessToken]
  );

  /**
   * Exports corpus data as CSV with optional filters.
   * 
   * @param {Record<string, any>} [filters] - Optional filters to apply to export
   * @param {AbortSignal} [signal] - Optional abort signal for request cancellation
   * @returns {Promise<Blob>} CSV file blob
   * @throws {Error} If token is invalid
   */
  const exportCorpuses = useCallback(
    async (filters?: Record<string, any>, signal?: AbortSignal) => {
      const token = await getUserAccessToken();
      if (!token) throw new Error("Invalid token");

      const body: any = filters || {};

      const response = await fetchService.post<Blob>(CORPUS_EXPORT_API, {
        body,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        signal,
      });

      return response;
    },
    [fetchService, getUserAccessToken]
  );

  /**
   * Triggers retry for failed corpus ingestion.
   * Reprocesses a specific failed corpus document by ID.
   * 
   * @param {number} id - Corpus document ID to retry
   * @param {AbortSignal} [signal] - Optional abort signal for request cancellation
   * @returns {Promise<APIResponse<{message: string}>>} Retry response
   * @throws {Error} If token is invalid
   */
  const triggerCorpusRetry = useCallback(
    async (id: number, signal?: AbortSignal) => {
      const token = await getUserAccessToken();
      if (!token) throw new Error("Invalid token");

      const response = await fetchService.post<APIResponse<{ message: string }>>(
        CORPUS_TRIGGER_API,
        {
          body: {
            id,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          signal,
        }
      );

      return response;
    },
    [fetchService, getUserAccessToken]
  );

  return {
    getCorpusStats,
    getCorpuses,
    exportCorpuses,
    triggerCorpusRetry,
  };
};

export default useCorpusApiService;